import type { LotteryName, Participation, RenewType } from '../types';

/**
 * Registry for lottery-specific extra information extractors.
 *
 * Each lottery-specific file (e.g. megamillions.ts) registers extractors that
 * control what is shown across purchase surfaces:
 * - Participation-level extras (e.g. "Megaplier" badge in Your Numbers)
 * - Line-level extras (e.g. multiplier values per line)
 * - Purchase summary items (ticket- or participation-scoped)
 */

type ExtrasTicketContext = {
  lotteryName: LotteryName;
  renewType?: RenewType;
  duration: number;
  lineCount: number;
  dyjPlayed: boolean;
  options: string[];
};

type ExtrasContext = {
  ticket: ExtrasTicketContext;
  participation?: Participation;
};

type ParticipationExtractorData = {
  participation: Participation;
  options?: string[];
};

type LineExtractorData = {
  participation: Participation;
  lineNumber: number;
};

type ParticipationInfo = {
  translationKey: string;
  showIcon?: boolean;
};

type LineInfo = {
  translationKey: string;
  translationParams?: Record<string, string | number>;
};

type SummaryItemDescriptor = {
  id: string;
  translationKey: string;
  translationParams?: Record<string, string | number>;
};

type ParticipationExtractor = (data: ParticipationExtractorData) => ParticipationInfo | null;
type LineExtractor = (data: LineExtractorData) => LineInfo | null;
type PurchaseSummaryExtractor = (context: ExtrasContext) => SummaryItemDescriptor[];

type LotteryConfig = {
  extractParticipationInfo?: ParticipationExtractor;
  extractLineInfo?: LineExtractor;
  extractPurchaseSummaryItems?: PurchaseSummaryExtractor;
};

const lotteryConfigs: Partial<Record<LotteryName, LotteryConfig>> = {};

/**
 * Registers extractors for a specific lottery.
 * Each lottery-specific file should call this to register its extractors.
 */
function registerLottery(name: LotteryName, config: LotteryConfig) {
  lotteryConfigs[name] = config;
}

/**
 * Retrieves participation-level extra information (e.g., badges, icons).
 * Returns null if no extractor is registered or if the extractor returns null.
 */
function getParticipationInfo(lotteryName: LotteryName, participation: Participation, options?: string[]) {
  return lotteryConfigs[lotteryName]?.extractParticipationInfo?.({ participation, options }) ?? null;
}

/**
 * Retrieves line-level extra information (e.g., multiplier values, special indicators).
 * Returns null if no extractor is registered or if the extractor returns null.
 */
function getLineInfo(lotteryName: LotteryName, participation: Participation, lineNumber: number) {
  return lotteryConfigs[lotteryName]?.extractLineInfo?.({ participation, lineNumber }) ?? null;
}

function getLotteryPurchaseSummaryItems(context: ExtrasContext) {
  return lotteryConfigs[context.ticket.lotteryName]?.extractPurchaseSummaryItems?.(context) ?? [];
}

export { getLineInfo, getLotteryPurchaseSummaryItems, getParticipationInfo, registerLottery };
export type { ExtrasContext, ExtrasTicketContext, LineInfo, ParticipationInfo, SummaryItemDescriptor };
